import React from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, ShieldCheck } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { ChartInfoButton } from '@/components/shared/ChartInfoButton'

export interface DataTrustData {
  brokersHealthy: number
  brokersTotal: number
  scrapedLast7dPct: number
  scrapedLast7dCount: number
  activeProjectsCount: number
  ghostUnitsCount: number
  asOf: string | null
}

interface Props {
  data: DataTrustData | undefined
  loading?: boolean
}

function Row({ label, value, alert }: { label: string; value: React.ReactNode; alert?: boolean }) {
  return (
    <div className="flex items-center justify-between py-2 border-b last:border-b-0 border-secondary-100">
      <span className="text-sm text-secondary-600">{label}</span>
      <span
        className={`text-sm font-semibold ${alert ? 'text-red-600' : 'text-secondary-900'}`}
      >
        {value}
      </span>
    </div>
  )
}

export function DataTrustPanel({ data, loading }: Props) {
  const { canSeeMonitoring } = useAuth()

  if (loading) {
    return (
      <div className="card">
        <div className="h-5 w-40 bg-secondary-200 animate-pulse rounded mb-3" />
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-4 w-full bg-secondary-100 animate-pulse rounded my-2" />
        ))}
      </div>
    )
  }

  if (!data) return null

  const healthyPct = data.brokersTotal > 0
    ? Math.round((data.brokersHealthy / data.brokersTotal) * 100)
    : 100

  const asOfStr = data.asOf
    ? new Date(data.asOf).toLocaleString()
    : 'no scrape on record'

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-secondary-900 flex items-center gap-2">
          {healthyPct === 100 ? (
            <ShieldCheck className="h-4 w-4 text-green-600" />
          ) : (
            <AlertTriangle className="h-4 w-4 text-amber-600" />
          )}
          Data Trust
        </h3>
        <div className="flex items-center gap-2">
          {canSeeMonitoring && (
            <Link
              to="/monitoring"
              className="text-xs text-primary-600 hover:text-primary-800 underline"
            >
              Open monitoring
            </Link>
          )}
          <ChartInfoButton
            title="Data Trust"
            info={{
              definition:
                'Freshness and health signals for the scraping pipeline. Use this before acting on any analytics number — if the scrapers are stale, the numbers are stale too.',
              formula:
                'healthy broker % = healthy_broker_scrapers / total_broker_scrapers · scraped-in-7d % = projects with successful scrape in last 7d / active projects · ghost units = unit.last_scraped_at < project.last_scraped_at - 7d',
              calculation:
                'Healthy broker means its most recent scrape run succeeded. Ghost units are the DB\'s early-warning before the 15-day auto-cleanup. "Latest scrape" is the most recent successful scrape across all active projects.',
              source: 'project.last_successful_scrape_at, project.scrap_status, unit.last_scraped_at',
            }}
          />
        </div>
      </div>

      <Row
        label="Healthy broker scrapers"
        value={`${data.brokersHealthy} / ${data.brokersTotal} (${healthyPct}%)`}
        alert={healthyPct < 80}
      />
      <Row
        label="Active projects scraped in last 7 days"
        value={`${data.scrapedLast7dCount} / ${data.activeProjectsCount} (${Math.round(
          data.scrapedLast7dPct,
        )}%)`}
        alert={data.scrapedLast7dPct < 80}
      />
      <Row
        label="Ghost units (not seen in 10+ days)"
        value={data.ghostUnitsCount}
        alert={data.ghostUnitsCount > 0}
      />
      <Row label="Latest scrape" value={asOfStr} />
    </div>
  )
}
